import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { catchError, combineLatest, defer, distinctUntilChanged, EMPTY, filter, from, map, merge,
    Observable, of, shareReplay, take, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { ToastService } from '@snelstart/ui-toast';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import {
    IdentityProviderStatus,
    BokkzJrLogic,
    KolommenbalansService,
    PmClient,
    BokkzAlgDataService,
    BokkzJrDataService,
    JaarrapportageEntity,
    AdminEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import { AdministrationFormGroup, createAdministrationFormGroup } from './koppelen-rgs-admin.form';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-koppelen-rgs-admin',
    imports: [
        SharedUiDetailsModule,
        SharedUiButtonModule,
        SharedUiAlertModule,
        ReactiveFormsModule,
        SharedUiFormControlsModule,
        CommonModule,
    ],
    templateUrl: './koppelen-rgs-admin.component.html',
    styleUrls: ['./koppelen-rgs-admin.component.css'],
})
export class KoppelenRgsAdminComponent implements OnInit {
    algDataService = inject(BokkzAlgDataService);
    jrDataService = inject(BokkzJrDataService);
    jrLogicService = inject(BokkzJrLogic);
    pmClient = inject(PmClient);
    kolommenbalansService = inject(KolommenbalansService);
    toastService = inject(ToastService);
    formBuilder = inject(FormBuilder);
    destroyRef = inject(DestroyRef);

    formGroup: AdministrationFormGroup = createAdministrationFormGroup(this.formBuilder);

    client$ = this.algDataService.client.pipe(
        filter((client) => !!client?.id),
        distinctUntilChanged((previousClient, currentClient) =>
            currentClient.id === previousClient.id
        ),
        tap((client) => console.debug(`Client: "${client.name}"`, client)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    administrations$ = this.client$.pipe(
        map((client) => client.adminEntities ?? []),
        tap((administrations) => {
            if (administrations.length === 0) {
                console.debug('No administrations loaded');
            }

            if (administrations.length > 0) {
                console.debug(`Loaded ${administrations.length} administrations`);
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    administrationOptions$ = this.administrations$.pipe(
        map((administrations) =>
            administrations.map<SelectFormControlItem<number>>((administration) =>
                ({
                    value: administration.id!,
                    label: administration.name!,
                }) as SelectFormControlItem<number>
            )
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    selectedAdministrationId$ = merge(
        defer(() => of(this.formGroup.controls.administrationId.value)),
        this.formGroup.controls.administrationId.valueChanges,
    ).pipe(
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    selectedAdministration$ = combineLatest([
        this.administrations$,
        this.selectedAdministrationId$
    ]).pipe(
        map(([administrations, selectedAdministrationId]) =>
            administrations.find((administration) =>
                administration.id === selectedAdministrationId) ?? null,
        ),
        distinctUntilChanged((previousAdministration, currentAdministration) =>
            currentAdministration?.id === previousAdministration?.id
        ),
        tap((administration) => {
            if (administration) {
                console.debug(`Administration: "${administration.name}"`, administration);
            } else {
                console.debug('No administration selected');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    identityProvider$ = this.selectedAdministration$.pipe(
        switchMap((selectedAdministration) =>
            selectedAdministration?.providerId
                ? this.pmClient.getIdentityProvider$(selectedAdministration!.providerId)
                : of(null)
        ),
        distinctUntilChanged(
            (previousIdentityProvider, currentIdentityProvider) =>
                currentIdentityProvider?.id === previousIdentityProvider?.id,
        ),
        tap((identityProvider) => {
            if (identityProvider) {
                console.debug(`Identity Provider: "${identityProvider.name}"`, identityProvider);
            } else {
                console.debug('No identity provider loaded');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    identityProviderStatus$ = combineLatest([
        this.selectedAdministration$,
        this.identityProvider$
    ]).pipe(
        switchMap(([administration, identityProvider]) => {
            if (administration != null && identityProvider != null && administration.providerId === identityProvider.id) {
                if (identityProvider.requiresEnvironment) {
                    if (administration.environmentId) {
                        return this.pmClient.getIdentityProviderEnvironmentStatus$(identityProvider.id, administration.environmentId);
                    }

                    return of({ id: identityProvider.id, name: identityProvider.name, authenticated: false } as IdentityProviderStatus);
                }

                return this.pmClient.getIdentityProviderStatus$(identityProvider.id);
            }

            return of(null);
        }),
        tap((identityProviderStatus) => {
            if (identityProviderStatus != null) {
                console.debug(`Loaded "${identityProviderStatus.name}" identity provider status`, identityProviderStatus);
            } else {
                console.debug('No identity provider status loaded');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    jaarrapportage$ = this.jrDataService.jaarrapportageEntity.pipe(
        filter((jaarrapportage) => !!jaarrapportage?.id),
        distinctUntilChanged(
            (previousJaarrapportage, currentJaarrapportage) =>
                currentJaarrapportage.id === previousJaarrapportage.id
        ),
        tap(
            (jaarrapportage) =>
                console.debug(`Jaarrapportage: ${jaarrapportage.year}`, jaarrapportage)
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    importSnelStartBalanceButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.canImportSnelStartBalance$,
        onClick: () => this.importSnelStartBalance$,
        onError: (error) => console.error(error),
    });

    canImportSnelStartBalance$ = this.selectedAdministration$.pipe(
        take(1),
        map((selectedAdministration) => {
            if (!selectedAdministration) {
                this.toastService.showErrorMessage('Er is geen administratie geselecteerd.');
                return false;
            }

            return true;
        }),
    );

    importSnelStartBalance$ = combineLatest([
        this.client$,
        this.jaarrapportage$,
        this.selectedAdministration$
    ]).pipe(
        take(1),
        switchMap(([client, jaarrapportage, selectedAdministration]) => {
            console.info('Importing SnelStart administration', client, jaarrapportage, selectedAdministration);

            if (!client || !jaarrapportage || !selectedAdministration) {
                return EMPTY;
            }

            return from(
                this.kolommenbalansService.getUpdatedAangifteMetAdministratie(
                    selectedAdministration.administrationId!,
                    client.sstRelationId!,
                    jaarrapportage,
                    false,
                    client.sstPrimaryAdminId!,
                ),
            ).pipe(
                map(
                    (jaarrapportage) =>
                        [jaarrapportage as JaarrapportageEntity, selectedAdministration] as const
                )
            );
        }),
        tap(([jaarrapportage, selectedAdministration]) => {
            console.info('Jaarrapportage berekend: ', jaarrapportage);

            jaarrapportage.sstAdminId = selectedAdministration.administrationId!;

            this.jrDataService.updateEntity(jaarrapportage);

            this.toastService.showSuccessMessage('De administratie is succesvol ingelezen in de jaarrapportage.');
        }),
        catchError((error) => {
            console.error(error);

            if (error.error) {
                if (JSON.stringify(error.error).includes('RGS-code is leeg')) {
                    this.toastService.showErrorMessage(
                        'Het is helaas niet gelukt de administratie in te lezen in de Jaarrapportage. De foutmelding is als volgt: ' +
                            error.error +
                            '. Je kunt wel verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                    );
                } else {
                    this.toastService.showErrorMessage(
                        'Het is helaas niet gelukt de administratie in te lezen in de Jaarrapportage. De administratie is namelijk niet (volledig) RGS-ready. Pas de RGS-codes in de administratie eerst aan of ga verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                    );
                }
            } else {
                this.toastService.showErrorMessage(
                    'Helaas is er een technisch probleem waardoor de koppeling niet succesvol is verlopen. Wij werken hard aan een oplossing. Je kunt wel verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                );
            }

            return EMPTY;
        }),
    );

    importBalanceProviderBalanceButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.canImportBalanceProviderBalance$,
        onClick: () => this.importBalanceProviderBalance$,
        onError: (error) => console.error(error),
    });

    canImportBalanceProviderBalance$ = combineLatest([this.selectedAdministration$, this.identityProviderStatus$]).pipe(
        take(1),
        map(([selectedAdministration, identityProviderStatus]) => {
            if (!selectedAdministration) {
                this.toastService.showErrorMessage('Er is geen administratie geselecteerd.');
                return false;
            }

            const authenticated = !!identityProviderStatus?.authenticated;

            if (!authenticated) {
                this.toastService.showErrorMessage('U bent niet geautoriseerd om deze administratie in te lezen.');
            }

            return authenticated;
        }),
    );

    importBalanceProviderBalance$ = combineLatest([
        this.client$,
        this.jaarrapportage$,
        this.selectedAdministration$,
    ]).pipe(
        take(1),
        switchMap(([client, jaarrapportage, selectedAdministration]) => {
            if (!client || !jaarrapportage || !selectedAdministration?.providerId) {
                return EMPTY;
            }

            console.info(
                `Importing ${selectedAdministration.name} administration`,
                client,
                jaarrapportage,
                selectedAdministration,
            );

            if (selectedAdministration.environmentId) {
                return this.pmClient.postBalanceProviderBalancesEnvironment(
                    client.sstRelationId!,
                    selectedAdministration.providerId!,
                    selectedAdministration.environmentId!,
                    selectedAdministration.administrationId!,
                    false,
                    jaarrapportage
                ).pipe(
                    map((jaarrapportage) =>
                        [jaarrapportage as JaarrapportageEntity, selectedAdministration] as const
                    )
                );
            } else {
                return this.pmClient.postBalanceProviderBalances(
                    client.sstRelationId!,
                    selectedAdministration.providerId!,
                    selectedAdministration.administrationId!,
                    false,
                    jaarrapportage
                ).pipe(
                    map((jaarrapportage) =>
                        [jaarrapportage as JaarrapportageEntity, selectedAdministration] as const
                    )
                );
            }
        }),
        tap(([jaarrapportage, selectedAdministration]) => {
            console.debug('Jaarrapportage berekend: ', jaarrapportage);

            this.jrLogicService.getCalculation(jaarrapportage);

            jaarrapportage.sstAdminId = selectedAdministration.administrationId ?? undefined;

            this.jrDataService.updateEntity(jaarrapportage);

            this.toastService.showSuccessMessage('De administratie is succesvol ingelezen in de jaarrapportage.');
        }),
        catchError((error) => {
            console.error(error);
            this.toastService.showErrorMessage(
                'Het is helaas niet gelukt de administratie in te lezen in de Jaarrapportage. Je kunt wel verder met de Jaarrapportage door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
            );
            return EMPTY;
        }),
    );

    public ngOnInit(): void {
        this.administrations$
            .pipe(
                map((administrations) =>
                    administrations.length === 1 ? administrations[0] : null
                ),
                filter((administration) => administration != null),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((administration) => {
                if (administration.id !== this.formGroup.controls.administrationId.value) {
                    console.debug(`Selecting "${administration.name}"`, administration);
                    this.formGroup.controls.administrationId.setValue(administration.id!);
                }
            });
    }
}
